'use client';

import { MinusIcon, PlusIcon } from '@heroicons/react/24/outline';
import useCart from 'components/hooks/use-cart';
import { AddToCart } from 'components/product/add-to-cart';
import { withSuspense } from 'components/with-suspense';
import { VercelProduct as Product, ProductVariant } from 'lib/bigcommerce/types';
import { useEffect, useState } from 'react';
import './buy-box.css';
import { ProductPrice } from './price';

const iconSize = '16';
const stickyBreakPoint = 992;

function BuyBoxComponent({ 
  product,
  showQuantitySelector,
  showStickyBar 
}: { 
  product: Product;
  showQuantitySelector: boolean;
  showStickyBar: boolean;
}) {
  // Get selected variant name
  const [selectedVariantName, setSelectedVariantName] = useState<string>('');
  useEffect(() => {
    const updateSelectedVariantText = () => {
      const selectedVariant = document.querySelector('.variant-option.variant-option-selected');
      if (selectedVariant) {
        setSelectedVariantName(selectedVariant.textContent || '');
      } else {
        setSelectedVariantName('');
      }
    };

    updateSelectedVariantText();

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const target = mutation.target as Element;
          if (target.classList.contains('variant-option')) {
            updateSelectedVariantText();
          }
        }
        if (mutation.type === 'childList') {
          mutation.addedNodes.forEach((node) => {
            if (node instanceof Element && node.classList?.contains('variant-option-selected')) {
              updateSelectedVariantText();
            }
          });
        }
      });
    });

    const variantOptions = document.querySelectorAll('.variant-option');
    variantOptions.forEach((element) => {
      observer.observe(element, {
        attributes: true,
        attributeFilter: ['class']
      });
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class']
    });

    return () => observer.disconnect();
  }, []);

  // Check if product has Inventory Tracking enabled
  const [inventoryTracking, setInventoryTracking] = useState(false);
  useEffect(() => {
    // Based on value used in apps/nextjs-bigcommerce/lib/bigcommerce/mappers.ts for the availableToSell default values
    if (product.inventory.aggregated.availableToSell === 987654) {
      setInventoryTracking(false)
    } else {
      setInventoryTracking(true)
    }
  }, [inventoryTracking, product.inventory.aggregated.availableToSell]);

  // Get selected variant, and variant max quantity
  const [variantMaxQty, setVariantMaxQty] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | undefined>(undefined);
  useEffect(() => {
    if (selectedVariantName) {
      let foundQty = 0;
      
      product.variants.forEach(variant => {
        if (variant.selectedOptions) {
          variant.selectedOptions.forEach(option => {
            if (option.value === selectedVariantName) {
              foundQty = variant.inventory.aggregated.availableToSell;
              setSelectedVariant(variant);
            }
          });
        }
      });
      
      setVariantMaxQty(foundQty);
    } else {
      setVariantMaxQty(0);
    }
  }, [selectedVariantName, product.variants]);

  // Get cart, and cart variant quantity/max
  const { cart } = useCart();
  const [cartHasVariantMax, setCartHasVariantMax] = useState(false);
  const [cartVariantQty, setCartVariantQty] = useState(0);
  useEffect(() => {
    if (!cart?.lines || !selectedVariant) {
      setCartHasVariantMax(false);
      setCartVariantQty(0);
      return;
    }

    const cartItem = cart.lines.find(item => item.sku === selectedVariant.sku);
    if (cartItem) {
      const cartQty = cartItem.quantity;
      const hasMaxQuantity = cartQty === variantMaxQty;

      setCartVariantQty(cartQty);
      setCartHasVariantMax(hasMaxQuantity);
    }
  }, [cart?.lines, selectedVariant, variantMaxQty]);

  // Check if inventory tracking is by product, or variant
  const variantsInStockAndZero = [];
  product.variants.map(variant => {
    if (variant.inventory.isInStock === true && variant.inventory.aggregated.availableToSell === 0) {
      variantsInStockAndZero.push(variant);
    }
  });

  let inventoryTrackingType = 'disabled';
  if (!product?.inventory.hasVariantInventory) {
    inventoryTrackingType = 'product';
  } else if (product?.inventory.hasVariantInventory && product.variants.length !== variantsInStockAndZero.length) {
    inventoryTrackingType = 'product';
  } else if (product?.inventory.hasVariantInventory && product.variants.length === variantsInStockAndZero.length) {
    inventoryTrackingType = 'variant';
  }

  // Max quantity available
  const maxQuantity = product.variants.length > 1 && inventoryTracking && inventoryTrackingType === 'variant' ? variantMaxQty : product.variants.length > 1 && inventoryTracking && inventoryTrackingType === 'product' ? product.inventory?.aggregated.availableToSell : product.inventory?.aggregated.availableToSell;
  // Update text input value
  const [inputValue, setValue] = useState(1);

  // Handle value & max quantity
  const handleValue = () => {
    if (inputValue <= 0) {
      setValue(1);
    }
  
    if (inputValue > maxQuantity) {
      setValue(maxQuantity);
      return;
    }
  }

  // Handle text input
  const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
    inputValue <= 0 ? setValue(1) : setValue(Number(event.target.value));
  }

  // Increment input value
  const increment = () => {
    setValue(inputValue + 1);
  }

  // Decrement input value
  const decrement = () => { 
    setValue(inputValue - 1);
    handleValue();
  }

  // Desktop Sticky Bar
  const stickyBarObserver = (stickyBar:Element | null, utilityMenu:Element | null, buyBox:Element | null) => {

    //Reset if in Mobile Mode
    if(utilityMenu && utilityMenu.className.includes('product-sticky-menu')) {
        utilityMenu.classList.remove('product-sticky-menu');
        if(stickyBar) utilityMenu.after(stickyBar);
    }

    // Observe when buy box is in viewport
    if(stickyBar && !stickyBar.className.includes('product-sticky-bar-observe')) {
        const observer = new IntersectionObserver(entries => {
        entries.forEach(entry => {
            entry.isIntersecting === true
            ? stickyBar?.setAttribute('style', `opacity: 0; visibility: hidden;`)
            : stickyBar?.setAttribute('style', `opacity: 1; visibility: visible;`)
        });
        });
        buyBox !== null ? observer.observe(buyBox) : null;
        stickyBar?.classList.add('product-sticky-bar-observe');
    }
  }

  // Mobile Sticky Bar
  const stickyBarCombiner = (stickyBar:Element | null, utilityMenu:Element | null, buyBox:Element | null) => {
    if(utilityMenu && stickyBar && !utilityMenu.className.includes('product-sticky-menu')) {
        utilityMenu.classList.add('product-sticky-menu');
        utilityMenu.prepend(stickyBar);
    }
  }

  let notifyMe = false;
  product && product.productMetafields.map((field: any) => {
    field.key === 'notify-me' && field.value === 'true'
      ? notifyMe = true
      : notifyMe = false
  });

  useEffect(() => {
    const handleResize = () => {
        const stickyBar = document.querySelector('.product-sticky-bar');
        const utilityMenu = document.getElementById('sticky-utility-menu');
        const buyBox = document.querySelector('.product-buy-box');

        if (window.innerWidth >= stickyBreakPoint) {
            stickyBarObserver(stickyBar, utilityMenu, buyBox);
        } else {
            stickyBarCombiner(stickyBar, utilityMenu, buyBox);
        }
    }
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => {
     window.removeEventListener('resize', handleResize);
    };
  });

  const isInStock = product.inventory.isInStock;

  return (
    <>
      <div className="product-buy-box">
        {
          product.inventory.isInStock === true 
          && product.inventory.aggregated.availableToSell > 1 
          && showQuantitySelector === true
          && !notifyMe
            ? <div className="product-quantity-box">
                <div className="product-quantity-selector">
                  <button 
                    className={`product-quantity-button product-quantity-decrease`}
                    aria-label={`Decrease Quantity`}
                    onClick={decrement}
                  >
                    <MinusIcon width={iconSize} height={iconSize} />
                  </button>
                  <input 
                    type="text" 
                    id="product-quantity"
                    value={`${inputValue}`}
                    onChange={handleInput}
                  />
                  <button 
                    className={`product-quantity-button product-quantity-increase`}
                    aria-disabled={inputValue > maxQuantity ? true : false}
                    aria-label={`Increase Quantity`}
                    disabled={inputValue > maxQuantity || cartHasVariantMax || (cartVariantQty + inputValue - 1) === maxQuantity ? true : false}
                    onClick={increment}
                  >
                    <PlusIcon width={iconSize} height={iconSize} />
                  </button>
                </div>
                {
                  maxQuantity > 0 && inputValue > maxQuantity && !cartHasVariantMax
                    ? <div className="product-quantity-error">
                        Only {maxQuantity} available
                      </div>
                    : null
                }
                {
                  cartVariantQty > 0
                    ? <div className="product-quantity-error">
                        {
                          cartHasVariantMax
                            ? `Only ${maxQuantity} available, ${cartVariantQty} in your cart`
                            : (cartVariantQty + inputValue - 1) === maxQuantity
                            ? `Only ${maxQuantity} available, ${cartVariantQty} in your cart`
                            : null
                        }
                      </div>
                    : null
                }
              </div>
            : null
        }
        <AddToCart 
          product={product}
          variants={product.variants} 
          availableForSale={product.availableForSale} 
          quantity={inputValue} 
          isInStock={isInStock} 
          disabled={
            !notifyMe && !isInStock ? false
              : !notifyMe && inputValue > maxQuantity || cartHasVariantMax || (cartVariantQty + inputValue - 1) === maxQuantity
                ? true 
                : false
          }
        />
      </div>
      {
        showStickyBar === true 
          ? <section className="product-sticky-bar">
              <div className="product-sticky-bar-content">
                <h2 className="product-sticky-bar-heading">{product.title}</h2>
                <ProductPrice 
                  hidePromo={true}
                  product={product} 
                />
              </div>
              <AddToCart 
                product={product}
                variants={product.variants} 
                availableForSale={product.availableForSale} 
                quantity={inputValue} 
                isInStock={product.inventory.isInStock} 
                disabled={
                  !notifyMe && !isInStock ? false
                    : !notifyMe && inputValue > maxQuantity || cartHasVariantMax || (cartVariantQty + inputValue - 1) === maxQuantity
                      ? true 
                      : false
                }
              />
            </section>
          : null
      }
    </>
  );
}

const BuyBoxSkeleton = (
  <></>
);

export const BuyBox = withSuspense(BuyBoxComponent, BuyBoxSkeleton);
